import React from "react";
import styled from "styled-components";

import ContractMessageRow from "./ContractMessageRow";
import { buildContractActionMenuItems } from "./contractActionMenu";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../date-display/DateDisplay";
import SubcontractContractPdfPreview, {
  type SubcontractPdfPreviewState,
} from "../offer-wizard/SubcontractContractPdfPreview";
import type { DmMessage } from "../../app/services/dmMessages";
import type { DmPendingAmendment } from "../../app/services/dmThreads";
import type { ContractAmendedSystemContent } from "../../types/systemContent";
import { describeAmendmentChanges } from "../../utils/contractAmendment";

interface ContractAmendedTimelineItemProps {
  message: DmMessage;
  content: ContractAmendedSystemContent;
  isOurs: boolean;
  showAvatar: boolean;
  currentUserId?: number | string;
  /**
   * この更新が成立した日時（= 受託者が承認した時刻。メッセージの作成日時）。
   * 提案の時点ではこのアイテム自体が存在しないので、常に締結側の時刻になる。
   */
  concludedAt?: string | null;
  /** この更新で成立した契約書 PDF。初回の契約書アイテムと同じくその場で読める。 */
  contractPdfPreview?: SubcontractPdfPreviewState | null;
  /** この版がまだ有効か。さらに更新されていれば `false`（「無効」表示）。 */
  isContractActive?: boolean;
  /**
   * 以下は **現在有効な版** のアイテムにだけ出す操作。契約を続けて更新できる
   * よう、初回の契約書アイテムと同じ 3 点メニューをここに持たせる。
   */
  onCloseRequest?: () => void;
  onContractUpdate?: () => void;
  hasActiveChildSubcontracts?: boolean;
  /** 提案中は「契約書を更新」を無効化するために使う（差分表示は別アイテム）。 */
  pendingAmendment?: DmPendingAmendment | null;
}

// ContractCard と同じ内側構成（親の吹き出しに馴染ませ、枠や背景は持たない）。
const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const MetaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetaRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 8px;
  align-items: baseline;
`;

const MetaLabel = styled.div.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const MetaValue = styled.div.attrs({ className: "bodytext-13" })`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
`;

const Previous = styled.span`
  color: var(--soremo-light-gray);
  text-decoration: line-through;
`;

const Arrow = styled.span`
  color: var(--soremo-light-gray);
`;

const Divider = styled.div`
  height: 1px;
  background: var(--soremo-border);
`;

const ContractFilePreview = styled.div`
  width: 100%;
`;

/**
 * 契約更新が成立したときのタイムラインアイテム。
 *
 * 初回の契約締結（`ContractTimelineItem`）と同じ見た目に揃える方針:
 * 通常メッセージと同じ吹き出しの中に、変更点 → 更新後の要点（納期・対価）→
 * 更新後の契約書 PDF を並べ、その場で契約書を読めるようにする。
 *
 * 「契約更新が成立」の告知は、初回の「オファー成立」と同じくこのアイテムの
 * 手前にシステムメッセージとして出す（`DM.tsx`）。
 */
const ContractAmendedTimelineItem: React.FC<
  ContractAmendedTimelineItemProps
> = ({
  message,
  content,
  isOurs,
  showAvatar,
  currentUserId,
  concludedAt,
  contractPdfPreview,
  isContractActive = true,
  onCloseRequest,
  onContractUpdate,
  hasActiveChildSubcontracts,
  pendingAmendment,
}) => {
  const rows = describeAmendmentChanges(content.changes);
  const hasContractPdf = !!contractPdfPreview;

  // 有効な版かつ送信者（委託者）のときだけ、続けて更新・クローズ相談できる。
  const showActions = isContractActive && isOurs;
  const menuItems = showActions
    ? buildContractActionMenuItems({
        onCloseRequest,
        onContractUpdate,
        hasActiveChildSubcontracts,
        pendingAmendment,
      })
    : [];

  return (
    <ContractMessageRow
      message={message}
      isOurs={isOurs}
      showAvatar={showAvatar}
      currentUserId={currentUserId}
      menuItems={menuItems}
    >
      <Inner data-testid="contract-amended-card">
        {/* 変更点だけを並べる。納期・対価の一覧は初回の契約書アイテムが持つので、
            変わっていない項目まで毎回繰り返さない。 */}
        <MetaList>
          {rows.map((row) => (
            <MetaRow key={row.field}>
              <MetaLabel>{row.label}</MetaLabel>
              <MetaValue>
                <Previous>{row.previous}</Previous>
                <Arrow>→</Arrow>
                <span>{row.next}</span>
              </MetaValue>
            </MetaRow>
          ))}
          {concludedAt && (
            <MetaRow>
              <MetaLabel>締結日時</MetaLabel>
              <MetaValue>
                <DateDisplay
                  date={concludedAt}
                  type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
                />
              </MetaValue>
            </MetaRow>
          )}
        </MetaList>

        {hasContractPdf && (
          <>
            <Divider />
            <ContractFilePreview>
              <SubcontractContractPdfPreview
                preview={contractPdfPreview as SubcontractPdfPreviewState}
                variant="message"
                statusLabel={isContractActive ? "締結済み" : "無効"}
                isSuperseded={!isContractActive}
              />
            </ContractFilePreview>
          </>
        )}

      </Inner>
    </ContractMessageRow>
  );
};

export default ContractAmendedTimelineItem;
